import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { joinUrls } from '@utils/urls';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export type RouteItem = { route: string; count: number; unique_users: number };
export type RoutesResponse = { data: RouteItem[]; total: number };

export type SessionItem = {
  session_id: string;
  user_id: number;
  started_at: string;
  ended_at: string;
  duration_minutes: number;
  events_count: number;
};
export type SessionsResponse = { data: SessionItem[]; total: number };

export type EventItem = {
  event: string;
  count: number;
  unique_users: number;
  daily: { date: string; count: number }[];
  meta: Record<string, unknown>[];
};
export type EventRow = {
  event: string;
  date: string;
  count: number;
  unique_users: number;
  meta: Record<string, unknown>[];
};
export type EventsResponse = { data: EventItem[]; total: number };

export type DateRange = { from: string; to: string };
export type AnalyticsTrigger = { companyId: number; dateRange: DateRange };

@Injectable()
export class AnalyticsSummaryService {
  #http = inject(HttpClient);

  getRoutes(companyId: number, body: DateRange): Observable<RoutesResponse> {
    return this.#http.post<RoutesResponse>(
      joinUrls(environment.urls.proxies, String(companyId), 'analytics', 'routes'),
      body,
    );
  }

  getSessions(companyId: number, body: DateRange): Observable<SessionsResponse> {
    return this.#http.post<SessionsResponse>(
      joinUrls(environment.urls.proxies, String(companyId), 'analytics', 'sessions'),
      body,
    );
  }

  getEvents(companyId: number, body: DateRange): Observable<{ data: EventRow[]; total: number }> {
    return this.#http.post<EventsResponse>(
      joinUrls(environment.urls.proxies, String(companyId), 'analytics', 'summary'),
      body,
    ).pipe(
      map(r => ({
        total: r.total,
        data: r.data.flatMap(item =>
          item.daily.map(d => ({
            event: item.event,
            date: d.date,
            count: d.count,
            unique_users: item.unique_users,
            meta: item.meta ?? [],
          })),
        ),
      })),
    );
  }
}
